import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { FiltersProvider } from "@/context/FiltersContext";
import { CreditsProvider } from "@/context/CreditsContext";
import { isFan, FAN_BLOCKED } from "@/lib/fan";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import VerifyMagicLink from "@/pages/VerifyMagicLink";
import ResetPassword from "@/pages/ResetPassword";
import Landing from "@/pages/Landing";
import ContactUs from "@/pages/ContactUs";
import { Privacy, Terms } from "@/pages/LegalPage";
import NewSub from "@/pages/NewSub";
import CombinedView from "@/pages/CombinedView";
import DrawView from "@/pages/DrawView";
import { MyRodeos, Deadlines } from "@/pages/Simple";
import Directory from "@/pages/Directory";
import Season from "@/pages/Season";
import Stock from "@/pages/Stock";
import Stats from "@/pages/Stats";
import Planner from "@/pages/Planner";
import RouteMapper from "@/pages/RouteMapper";
import Admin from "@/pages/Admin";
import AdminDashboard from "@/pages/AdminDashboard";
import ReviewQueue from "@/pages/ReviewQueue";
import Goals from "@/pages/Goals";
import Standings from "@/pages/Standings";
import Cfr from "@/pages/Cfr";
import RodeoCounts from "@/pages/RodeoCounts";
import Results from "@/pages/Results";
import TradeList from "@/pages/TradeList";
import Bank from "@/pages/Bank";
import Roadmap from "@/pages/Roadmap";
import PRCA from "@/pages/PRCA";
import HelpGuide from "@/pages/HelpGuide";
import EntryDates from "@/pages/EntryDates";
import NfrCutline from "@/pages/NfrCutline";
import Today from "@/pages/Today";
import FanHome from "@/pages/FanHome";
import FanSchedule from "@/pages/FanSchedule";
import Movers from "@/pages/Movers";
import Payouts from "@/pages/Payouts";
import MyRiders from "@/pages/MyRiders";
import WeeklyDiscovery from "@/pages/WeeklyDiscovery";
import Wallet from "@/pages/Wallet";
import ChoosePlan from "@/pages/ChoosePlan";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";

function Protected({ children, adminOnly, reviewerOnly }) {
  const { user, ready } = useAuth();
  const loc = useLocation();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (!user) {
    try { sessionStorage.setItem("rp_post_login", window.location.pathname + window.location.search); } catch (e) {}
    return <Navigate to="/login" replace />;
  }
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  if (reviewerOnly && user.role !== "reviewer" && user.role !== "admin") return <Navigate to="/" replace />;
  // Fans can't reach contestant-only tools even by typing the URL.
  if (isFan(user) && FAN_BLOCKED.has(loc.pathname)) return <Navigate to="/today" replace />;
  return <Layout>{children}</Layout>;
}

// Home ("/" and "/today") — spectator-first for fans, contestant dashboard otherwise.
function HomeScreen() {
  const { user } = useAuth();
  return isFan(user) ? <FanHome /> : <Today />;
}

function Home() {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-slate-400 bg-[#0C1017]">Loading…</div>;
  if (user) return <Protected><HomeScreen /></Protected>;
  return <Landing />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <FiltersProvider>
          <CreditsProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/auth/verify" element={<VerifyMagicLink />} />
            <Route path="/auth/reset" element={<ResetPassword />} />
            <Route path="/welcome" element={<Landing />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/choose-plan" element={<Protected><ChoosePlan /></Protected>} />
            <Route path="/newsub" element={<NewSub />} />
            <Route path="/" element={<Home />} />
            <Route path="/calendar" element={<Protected><CombinedView mode="calendar" /></Protected>} />
            <Route path="/table" element={<Protected><CombinedView mode="table" /></Protected>} />
            <Route path="/map" element={<Protected><CombinedView mode="map" /></Protected>} />
            <Route path="/new-view" element={<Protected><CombinedView mode="table" /></Protected>} />
            <Route path="/directory" element={<Protected><Directory /></Protected>} />
            <Route path="/season" element={<Protected><Season /></Protected>} />
            <Route path="/stock" element={<Protected><Stock /></Protected>} />
            <Route path="/stats" element={<Protected><Stats /></Protected>} />
            <Route path="/planner" element={<Protected><Planner /></Protected>} />
            <Route path="/route-mapper" element={<Protected><RouteMapper /></Protected>} />
            <Route path="/my-rodeos" element={<Protected><MyRodeos /></Protected>} />
            <Route path="/deadlines" element={<Protected><Deadlines /></Protected>} />
            <Route path="/entry-dates" element={<Protected><EntryDates /></Protected>} />
            <Route path="/nfr-cutline" element={<Protected><NfrCutline /></Protected>} />
            <Route path="/today" element={<Protected><HomeScreen /></Protected>} />
            <Route path="/schedule" element={<Protected><FanSchedule /></Protected>} />
            <Route path="/movers" element={<Protected><Movers /></Protected>} />
            <Route path="/payouts" element={<Protected><Payouts /></Protected>} />
            <Route path="/riders" element={<Protected><MyRiders /></Protected>} />
            <Route path="/weekly" element={<Protected><WeeklyDiscovery /></Protected>} />
            <Route path="/wallet" element={<Protected><Wallet /></Protected>} />
            <Route path="/guide" element={<Protected><HelpGuide /></Protected>} />
            <Route path="/rodeo/:id" element={<Protected><DrawView /></Protected>} />
            <Route path="/pro" element={<Navigate to="/choose-plan" replace />} />
            <Route path="/goals" element={<Protected><Goals /></Protected>} />
            <Route path="/standings" element={<Protected><Standings /></Protected>} />
            <Route path="/cfr" element={<Protected><Cfr /></Protected>} />
            <Route path="/rodeo-counts" element={<Protected><RodeoCounts /></Protected>} />
            <Route path="/results" element={<Protected><Results /></Protected>} />
            <Route path="/trades" element={<Protected><TradeList /></Protected>} />
            <Route path="/bank" element={<Protected><Bank /></Protected>} />
            <Route path="/roadmap" element={<Protected><Roadmap /></Protected>} />
            <Route path="/prca" element={<Protected><PRCA /></Protected>} />
            <Route path="/settings" element={<Protected adminOnly><Admin /></Protected>} />
            <Route path="/review" element={<Protected reviewerOnly><ReviewQueue /></Protected>} />
            <Route path="/admin" element={<Protected adminOnly><AdminDashboard /></Protected>} />
            <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
            <Route path="/payment/cancel" element={<Protected><PaymentCancel /></Protected>} />
          </Routes>
          </CreditsProvider>
          </FiltersProvider>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
